import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  SxProps,
  Typography,
} from '@mui/material'
import { useState } from 'react'

import {
  instructionSummaryApi,
  RelatedRecord,
} from 'hooks/api/instructionSummary'

interface RelatedRecordsAccordionProps {
  summaryId: string
}

/**
 * 関連記録アコーディオンコンポーネント
 */
export function RelatedRecordsAccordion({
  summaryId,
}: RelatedRecordsAccordionProps) {
  const [records, setRecords] = useState<RelatedRecord[]>([])
  const [loading, setLoading] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const api = instructionSummaryApi()

  const handleExpand = async (expanded: boolean) => {
    if (!expanded || loaded) return

    try {
      setLoading(true)
      setError(null)
      const res = await api.getRelatedRecords(summaryId)
      setRecords(res.data.records || [])
      setLoaded(true)
    } catch {
      setError('関連記録の取得に失敗しました')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Accordion
      sx={accordionStyle}
      onChange={(_, expanded) => handleExpand(expanded)}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="body2" color="text.secondary">
          📎 元になった巡察記録を見る
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        {loading && (
          <Box sx={centerBox}>
            <CircularProgress size={24} />
          </Box>
        )}

        {error && <Alert severity="error">{error}</Alert>}

        {!loading && !error && records.length === 0 && loaded && (
          <Typography variant="body2" color="text.secondary">
            紐付いた記録がありません
          </Typography>
        )}

        {!loading && !error && records.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {records.map((record) => (
              <Card
                key={record.instructionRecordId}
                variant="outlined"
                sx={relatedRecordCardStyle}
              >
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    {record.instructedOn} / {record.instructor} / {record.place}
                  </Typography>

                  {record.instructionNote && (
                    <Box sx={{ mt: 1 }}>
                      <Typography
                        variant="caption"
                        color="primary"
                        fontWeight="bold"
                      >
                        【指導事項】
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}
                      >
                        {record.instructionNote}
                      </Typography>
                    </Box>
                  )}

                  {record.privateNote && (
                    <Box sx={{ mt: 1.5 }}>
                      <Typography
                        variant="caption"
                        color="primary"
                        fontWeight="bold"
                      >
                        【その他の会話内容・共有事項】
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}
                      >
                        {record.privateNote}
                      </Typography>
                    </Box>
                  )}

                  {!record.instructionNote && !record.privateNote && (
                    <Typography variant="body2" color="text.secondary">
                      （記録なし）
                    </Typography>
                  )}
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  )
}

const centerBox: SxProps = {
  display: 'flex',
  justifyContent: 'center',
  py: 4,
}

const accordionStyle: SxProps = {
  mt: 2,
  '&:before': {
    display: 'none',
  },
  backgroundColor: 'grey.50',
}

const relatedRecordCardStyle: SxProps = {
  backgroundColor: 'background.paper',
}
